import React, { useState, useEffect, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from 'classnames';
import _ from "lodash";
import { HeaderUserIcon } from "@netapp/bxp-design-system-react/icons/monochrome";
import { UserSettingsIcon } from "@netapp/bxp-style/react-icons/Action";
import styles from "./../../MainHeader.module.scss";
import userMenuStyles from "../../headerMenus/UserMenu.module.scss";
import SlidingWidget from "../SlidingWidget";
import {
    StyledLink, Button, Span, Toggle, Tag, Tooltip, FormFieldInputNew, InlineLoader, Text,
    ExternalLink} from "@netapp/bxp-design-system-react";
import buttons from "@netapp/bxp-design-system-react/styles/buttons.module.scss";
import { useTranslation } from "react-i18next";
import { allMenus as i18nNamespace } from "../../../../../i18n/namespace";
import { UserMFADetails } from "../../../../../App";
import { EmailIcon } from "@netapp/bxp-style/react-icons/Notification";
import OrgAdminDetails from "../../claimOrgAdmin/OrgAdminDetails";
import { isValidUnicodeUserName } from 'utils/validation';
import { isVirtualTourEnabled, enableVirtualTourMode, disableVirtualTourMode, showSimulatorToggleInUserMenu } from "../../../../../offline/utils";
import SafeModeUserRoleElevateToggle from "./components/SafeModeUserRoleElevateToggle";
import UserTokenModal from "./components/UserTokenModal";
import { isNcl } from "../../../../../utils/app/utils";

const EXTERNAL_LINKS = {
    REPORT_AN_ISSUE: "https://confluence.ngage.netapp.com/spaces/UMF/pages/1085918361/BlueXP+Demo+Issues+Tracking"
};

const StaticDetails = ({
    children,
    label,
    value,
}) => {
    return (
        <div className={userMenuStyles["detail"]}>
            <div className={classNames(styles["static-details"])}>
                <div style={{ width: '100%' }}>
                    <div className={userMenuStyles["details-label"]}>
                        {label}
                        <div>
                            {children}
                        </div>
                    </div>
                    <div className={classNames(userMenuStyles["details-value"], styles["info"])}>
                        {value}
                    </div>
                </div>
            </div>
        </div>
    );
};

const DarkTheme = () => {
    const dispatch = useDispatch();
    const isActive = useSelector(state => !!state.features.active["Platform.BlueXP/DarkTheme"]);
    const { t } = useTranslation(i18nNamespace);
    return <div className={userMenuStyles["detail"]}>
        <div style={{ marginBottom: 8 }}>
            <Span style={{ marginRight: 8 }}>{t("dark_theme")}</Span>
            <Tag color="chart-9">{t("beta")}
                <Tooltip>
                    {isActive ? <>
                        <div>{t("there_is_a_crack_a_crack_in_everything")}</div>
                        <div>{t("thats_how_the_light_gets_in")}</div>
                    </> : <>
                        <div>{t("you_want_it_darker")}</div>
                        <div>{t("we_kill_the_flame")}</div>
                    </>}
                </Tooltip>
            </Tag>
        </div>
        <Toggle value={isActive} ariaLabel={t("dark_theme")} toggle={() => {
            dispatch({
                type: "FEATURES:TOGGLE-FEATURE",
                payload: {
                    feature: "Platform.BlueXP/DarkTheme"
                }
            })
        }} />
    </div>
}
/**
 * VirtualTourMode component is used to toggle the offline mode.
 * @returns {JSX.Element}
 */
const VirtualTourToggle = ({ isActive, selectedOrgId, legacyId }) => {
    const { t } = useTranslation(i18nNamespace);
    return <div>
        <Toggle value={isActive} ariaLabel={t("virtual_tour_toggle")} toggle={() => {
            if (isActive) {
                disableVirtualTourMode();
            }
            else {
                enableVirtualTourMode({ orgId: selectedOrgId, legacyId });
            }
        }} />
    </div>
}

const UserNameEditForm = ({ handleNameChange, handleCancel, handleSubmit, userName, error, isLoading }) => {
    const { t } = useTranslation(i18nNamespace);
    return (
        <div className={styles["edit-name-form"]}>
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <div className={classNames(styles["content"])}>
                    <div className={styles["input-restrict"]}>
                        <FormFieldInputNew
                            type="text"
                            name="Name"
                            label={t("name")}
                            value={userName}
                            onChange={event => handleNameChange(event)}
                            errorMessage={error}
                            showError={!!error}
                        />
                    </div>
                    <div className={styles["button-wrapper"]}>
                        <Button submit type="text" isSubmitting={isLoading}>{t("save")}</Button>
                        <button onClick={(e) => {
                            e.stopPropagation();
                            handleCancel(false)
                        }}
                            className={classNames(buttons["text-secondary"], styles["button-space"])}>
                            {t("cancel")}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

const NameEmailDetails = ({ name, email, isAIDP }) => {
    const displayName = isAIDP ? decodeURIComponent(name) : name
    return <div className={classNames(userMenuStyles['name-details'])}>
        <Text ellipsis={true} title={displayName}>{displayName}</Text>
        {!isAIDP && <div className={classNames(userMenuStyles['email-details'])}>
            <EmailIcon color={'text'} size={16} />
            <Text ellipsis={true} title={email}>{email}</Text>
        </div>}
    </div>
}

export default () => {
    const dispatch = useDispatch();
    const { name, email, company, isNetAppInternal, isFederatedUser, getUserNamePending, getUserNameShowError, userId } = useSelector(state => state.auth);
    const isActive = useSelector(state => state.menu.activeWidget);
    const metadataEngineVersion = useSelector(state => state.app.supportServices?.metadataEngineVersion ?? null);
    const [editName, setEditName] = useState(false);
    const [userName, setUserName] = useState(name);
    const [error, setError] = useState('');
    const { t } = useTranslation(i18nNamespace);
    const { selectedOrgId, selectedOrg: { legacyId } = {}, user } = useSelector(state => state.tenancy);
    const isDarkSite = process.env.REACT_APP_IS_DARK === 'true';
    const isAIDP = process.env.REACT_APP_IS_AIDP === "true";
    const { isMFAFeatureFlagEnabled, isClaimOrgFeatureFlagEnabled, isSafeModeFeatureFlagEnabled } = useSelector((state) => ({
        isMFAFeatureFlagEnabled: state.features.active['NetApp.MFA/*'] ?? false,
        isClaimOrgFeatureFlagEnabled: state.features.active['NetApp.TenancyV4/claimOrg'] ?? false,
        isSafeModeFeatureFlagEnabled: state.features.active['NetApp.TenancyV4/safemode'] ?? false
    }));
    const [isMFAMgmtAllowed, setIsMFAMgmtAllowed] = useState(false);
    const [canClose, setCanClose] = useState(true);
    const isSaaSUI = process.env.REACT_APP_IS_SAAS === 'true';
    const isVirtualTourMode = isVirtualTourEnabled();
    const showSimulatorToggle = showSimulatorToggleInUserMenu();
    const isDemo = useSelector(state => state.tenancy.isDemo);
    const isFederatedGroupEnabled = useSelector(state => state.features.active["NetApp.TenancyV4/federatedGroups"] ?? false);
    const [showUserTokenModal, setShowUserTokenModal] = useState(false);

    useEffect(() => {
        const mfaInSaaS = isSaaSUI && !isFederatedUser && isMFAFeatureFlagEnabled;
        const mfaInNcl = isNcl() && _.some(user?.tags, tag => tag['internal:bxp:loginUserType'] === 'local');
        setIsMFAMgmtAllowed(mfaInSaaS || mfaInNcl);
    }, [isMFAFeatureFlagEnabled, isSaaSUI, isFederatedUser, user]);

    useEffect(() => {
        if (!isActive || (!getUserNamePending && !getUserNameShowError)) {
            setEditName(false);
            setUserName(name);
        }
    }, [isActive, getUserNamePending, getUserNameShowError]);

    useEffect(() => {
        if (getUserNameShowError) {
            setError(t("failed_to_update_the_name"));
        }
    }, [getUserNameShowError]);


    const handleNameChange = (event) => {
        setUserName(event.target.value);
    }

    const handleShowHideUserName = (isEditUserName) => {
        setEditName(isEditUserName);
        setError('');
        if (!isEditUserName) {
            setUserName(name);
        }
    }

    const handleNameSubmit = async () => {
        const _userName = userName.trim();
        if (_userName.length < 1) {
            setError(t("name_is_required"));
            return;
        }

        if (_userName.length > 255) {
            setError(t("name_cannot_be_more_than_255_characters"));
            return;
        }

        if (!isValidUnicodeUserName(_userName)) {
            setError(t("name_validation_error"))
            return;
        }

        if (_userName !== name) {
            dispatch({
                type: "USER:UPDATE-USERNAME",
                payload: {
                    userName: _userName
                }
            })
        }
    }

    return <SlidingWidget
        widgetKey="user"
        menuTitle={t("user_settings")}
        MenuIcon={UserSettingsIcon}
        MenuActionButton={() => {
            return <Button color="secondary" onClick={() => dispatch({
                type: "AUTH:LOGOUT"
            })}>{t("logout")}</Button>
        }}
        isActive={isActive}
        triggerClassName={styles['widget-button']}
        triggerActiveClassName={[styles['widget-button-active']]}
        triggerChildren={<HeaderUserIcon size={24} display="block" />}
        canClose={() => canClose}
    >
        <div className={userMenuStyles["content"]}>
            {

                editName ? (
                    <UserNameEditForm
                        handleNameChange={handleNameChange}
                        handleCancel={handleShowHideUserName}
                        handleSubmit={handleNameSubmit}
                        userName={userName}
                        error={error}
                        isLoading={getUserNamePending}
                    />
                ) : (
                    <StaticDetails
                        label={t("name")}
                        value={NameEmailDetails({ name, email, isAIDP })}
                    >
                        {!isFederatedUser && !isDarkSite && (
                            <Button variant="text"
                                onClick={
                                    (e) => {
                                        e.stopPropagation();
                                        handleShowHideUserName(true);
                                    }
                                }>
                                {t("edit")}
                            </Button>
                        )}
                    </StaticDetails>
                )
            }
            {isAIDP && metadataEngineVersion && <StaticDetails label={t("metadata_engine_version")} value={metadataEngineVersion} />}
            {!isAIDP && <StaticDetails label={t("company")} value={company}>
                {isNetAppInternal && <StyledLink to="/netapp-internal" variant="text" onClick={() => {
                    dispatch({
                        type: "MENU:CLOSE-WIDGET"
                    })
                }}>{t("netApp_internal")}</StyledLink>}
            </StaticDetails>}
            {isSaaSUI && isClaimOrgFeatureFlagEnabled && !_.isNil(selectedOrgId) && <StaticDetails label={t("organization_admins")} value={(() => <OrgAdminDetails />)()}></StaticDetails>}
            {isMFAMgmtAllowed && <StaticDetails label={t("mfa")} value={(() => <Suspense fallback={<InlineLoader />}><UserMFADetails setCanClose={setCanClose} /></Suspense>)()}></StaticDetails>}
            {isFederatedGroupEnabled && isFederatedUser && <StaticDetails label={t("user_token")}>
                <Button variant="text"
                    dataTestId="user-token-view-btn"
                    onClick={() => setShowUserTokenModal(true)}>
                    {t("view")}
                </Button>    
            </StaticDetails>}
            {isSafeModeFeatureFlagEnabled && !isDarkSite && <SafeModeUserRoleElevateToggle />}
            {!isAIDP && <DarkTheme />}
            {
                ((isSaaSUI || isNcl()) && (isDemo || isNetAppInternal || isVirtualTourMode || showSimulatorToggle)) && !isAIDP &&
                <>
                    <StaticDetails label={t("virtual_tour_toggle")} value={<VirtualTourToggle isActive={isVirtualTourMode} selectedOrgId={selectedOrgId} legacyId={legacyId} />}>
                        {/* <ExternalLink variant="text" href={EXTERNAL_LINKS.REPORT_AN_ISSUE}>
                            {t("report_issue")}
                        </ExternalLink> */}
                    </StaticDetails>
                </>
            }
        </div>
        {showUserTokenModal && <UserTokenModal setShowUserTokenModal={setShowUserTokenModal} />}
    </SlidingWidget>
};
